// Sales on an item: weekly units over the last year and 30 / 90 / 365-day totals, from the synced
// per-item summary (sales_summary, rebuilt after each nightly pull from Shopify's analytics). Daily
// detail stays on the server, for planning and the MCP's sales_history tool.
import { useMemo, useState } from "react";
import type { Item, SalesSummary } from "../../../shared/db.gen.ts";
import { qty } from "../lib/format.ts";
import { useRow, useRows } from "../lib/sync.ts";
import { cx, StatTile } from "../ui/controls.tsx";
import type { Column } from "../ui/DataTable.tsx";
import { Section } from "../ui/RecordPanel.tsx";

const DAY = 86_400_000;
const shortDate = (day: string) => new Date(`${day}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const plusDays = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);

/** Units a day over the last 90 days, or since the first sale when that's more recent. */
export function perDay(s: SalesSummary) {
  const selling = s.first_sold ? Math.round((Date.parse(s.as_of) - Date.parse(s.first_sold)) / DAY) + 1 : 90;
  return s.units_90 / Math.max(1, Math.min(90, selling));
}

export function SalesSection({ item }: { item: Item }) {
  const summary = useRow("sales_summary", item.id);
  const pulled = useRows("sales_summary").length > 0;
  if (!summary) {
    // Until the first pull there's nothing to say; after it, a Shopify item with no row never sold.
    if (!pulled || item.source !== "shopify") return null;
    return (
      <Section title="Sales">
        <div className="px-2 pb-2 text-muted">No sales in Shopify's history.</div>
      </Section>
    );
  }
  const rate = perDay(summary);
  return (
    <Section title="Sales">
      <div className="grid grid-cols-4 gap-2 px-2">
        <StatTile label="30 days" value={qty(summary.units_30)} />
        <StatTile label="90 days" value={qty(summary.units_90)} />
        <StatTile label="365 days" value={qty(summary.units_365)} />
        <StatTile label="Per day" value={rate >= 10 ? qty(Math.round(rate)) : rate.toFixed(rate ? 2 : 0)} hint="Average over the last 90 days" />
      </div>
      <Weekly summary={summary} />
    </Section>
  );
}

/** 52 weekly bars, oldest first; hovering one names its week. Weeks with net returns show as empty. */
function Weekly({ summary }: { summary: SalesSummary }) {
  const [hover, setHover] = useState<number | null>(null);
  const weeks = summary.weekly;
  const max = Math.max(1, ...weeks);
  const weekOf = (i: number) => {
    const end = plusDays(summary.as_of, -7 * (weeks.length - 1 - i));
    return `${shortDate(plusDays(end, -6))} – ${shortDate(end)}`;
  };
  const caption =
    hover !== null
      ? `${weekOf(hover)}: ${qty(weeks[hover])} sold`
      : `Weekly, last 52 weeks · ${summary.last_sold ? `last sold ${shortDate(summary.last_sold)}` : "no sales yet"} · through ${shortDate(summary.as_of)}`;
  return (
    <div className="px-2 pt-2 pb-2">
      <div
        role="img"
        aria-label={`Units sold per week, last 52 weeks: ${weeks.join(", ")}`}
        className="flex h-12 items-end gap-px border-b border-line"
        onMouseLeave={() => setHover(null)}
      >
        {weeks.map((units, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: one bar per week position
          <div key={i} className="flex h-full flex-1 items-end" title={`${weekOf(i)}: ${units} sold`} onMouseEnter={() => setHover(i)}>
            <div
              className={cx("w-full rounded-t-[2px]", hover === i || (hover === null && i === weeks.length - 1) ? "bg-accent" : "bg-line-strong")}
              style={{ height: `${(Math.max(0, units) / max) * 100}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 truncate text-[11px] text-faint">{caption}</div>
    </div>
  );
}

/** "Sold 90d" for the Sell items table. */
export function useSoldColumn(): Column<Item> {
  const summaries = useRows("sales_summary");
  return useMemo(() => {
    const sold = new Map(summaries.map((s) => [s.id, s.units_90]));
    return {
      key: "sold_90",
      header: "Sold 90d",
      width: "90px",
      align: "right",
      value: (i) => sold.get(i.id) ?? null,
      cell: (i) => qty(sold.get(i.id)),
      groupCell: (rs) => qty(rs.reduce((n, r) => n + (sold.get(r.id) ?? 0), 0)),
    };
  }, [summaries]);
}
